import { ChangeEvent } from "react";
import { Show } from "shared/components";
import { ApartmentsComVariant } from "types/landlord/address";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { BoomBrandMark } from "./BoomBrandMark";
import { SourceCheckbox } from "./SourceCheckbox";
import { SourceLogo } from "./SourceLogo";
import { TreeRow } from "./TreeRow";
import { SyndicationState } from "./syndication";

const noop = () => undefined;

const isolate =
  (handler: () => void) => (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    handler();
  };

export const SyndicationChannelTree = ({
  syndication,
  unitListed,
  hasListing,
  zillowEnabled,
  apartmentsComEnabled,
  zumperEnabled,
  dwellsyEnabled,
  onToggleZillow,
  onToggleApartmentsCom,
  onPickApartmentsVariant,
  onToggleZumper,
  onToggleDwellsy
}: {
  syndication: SyndicationState;
  unitListed: boolean;
  hasListing: boolean;
  zillowEnabled: boolean;
  apartmentsComEnabled: boolean;
  zumperEnabled: boolean;
  dwellsyEnabled: boolean;
  onToggleZillow: () => void;
  onToggleApartmentsCom: () => void;
  onPickApartmentsVariant: (variant: ApartmentsComVariant) => void;
  onToggleZumper: () => void;
  onToggleDwellsy: () => void;
}) => {
  const sectionDisabled = !unitListed || !hasListing;
  const variantDisabled = sectionDisabled || !syndication.apartmentsCom;

  const variantChecked = (variant: ApartmentsComVariant) =>
    unitListed &&
    syndication.apartmentsCom &&
    syndication.apartmentsComVariant === variant;

  const pickVariant = (variant: ApartmentsComVariant) => () => {
    if (syndication.apartmentsComVariant === variant) return;
    onPickApartmentsVariant(variant);
  };

  return (
    <div className="space-y-3">
      <SourceCheckbox
        label="Portfolio page"
        icon={<BoomBrandMark />}
        checked={unitListed}
        disabled
        onChange={isolate(noop)}
      />
      <SourceCheckbox
        label="Listings page"
        icon={<BoomBrandMark />}
        checked={unitListed}
        disabled
        onChange={isolate(noop)}
      />
      <Show if={zumperEnabled}>
        <SourceCheckbox
          label="Zumper"
          icon={<SourceLogo id={IntegrationId.ZUMPER} />}
          checked={unitListed && syndication.zumper}
          disabled={sectionDisabled}
          onChange={isolate(onToggleZumper)}
        />
      </Show>
      <Show if={zillowEnabled}>
        <SourceCheckbox
          label="Zillow"
          icon={<SourceLogo id={IntegrationId.ZILLOW} />}
          checked={unitListed && syndication.zillow}
          disabled={sectionDisabled}
          onChange={isolate(onToggleZillow)}
        />
      </Show>
      <Show if={apartmentsComEnabled}>
        <div>
          <SourceCheckbox
            label="Apartments.com"
            icon={<SourceLogo id={IntegrationId.APARTMENTS_COM} />}
            checked={unitListed && syndication.apartmentsCom}
            disabled={sectionDisabled}
            onChange={isolate(onToggleApartmentsCom)}
          />
          <div className="mt-3 space-y-3 pl-8">
            <TreeRow spacing="base">
              <SourceCheckbox
                label="Single Family"
                checked={variantChecked("single_family")}
                disabled={variantDisabled}
                onChange={isolate(pickVariant("single_family"))}
              />
            </TreeRow>
            <TreeRow isLast spacing="base">
              <SourceCheckbox
                label="Multi Family"
                checked={variantChecked("multi_family")}
                disabled={variantDisabled}
                onChange={isolate(pickVariant("multi_family"))}
              />
            </TreeRow>
          </div>
        </div>
      </Show>
      <Show if={dwellsyEnabled}>
        <SourceCheckbox
          label="Dwellsy"
          icon={<SourceLogo id={IntegrationId.DWELLSY} />}
          checked={unitListed && syndication.dwellsy}
          disabled={sectionDisabled}
          onChange={isolate(onToggleDwellsy)}
        />
      </Show>
    </div>
  );
};
